"use client";

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Eye, Lock, Mail, Stamp, Timer } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useEffect, useState } from "react";

import type { ScriptProtectionShareModeId } from "@/content/security-mock-structure";
import { trustFloatingCardClass } from "@/features/security/security-theme";
import { useSecurityMockContent } from "@/hooks/security/useSecurityMockContent";

import { ScreenplayElement } from "./ScreenplayElement";

const SCREENPLAY_PAGE =
  "font-['Courier_New',Courier,monospace] text-[10.5px] leading-[1.6] tracking-[0.01em] sm:text-[11px]";

/** Keep share-mode cycling in sync with the floating card float period. */
const SHARE_LINK_CYCLE_S = 5;
const CYCLE_MS = SHARE_LINK_CYCLE_S * 1000;
const EXPIRY_CYCLE_MS = 1500;
const EXPIRY_DAYS = [7, 14, 30] as const;

const SHARE_MODE_ICONS: Record<ScriptProtectionShareModeId, LucideIcon> = {
  password: Lock,
  email: Mail,
  expiry: Timer,
  viewOnly: Eye,
  watermark: Stamp,
};

const SHARE_LINK_FLOAT_TRANSITION = {
  duration: SHARE_LINK_CYCLE_S,
  repeat: Infinity,
  repeatType: "reverse" as const,
  ease: [0.42, 0, 0.58, 1] as const,
};

export const ScriptProtectionVisual = () => {
  const prefersReducedMotion = useReducedMotion();
  const { scriptProtectionVisual } = useSecurityMockContent();
  const [modeIndex, setModeIndex] = useState(0);
  const [expiryIndex, setExpiryIndex] = useState(0);
  const [shareLinkEntered, setShareLinkEntered] = useState(
    Boolean(prefersReducedMotion),
  );

  const { shareModes, visibleScript, maskedScript } = scriptProtectionVisual;
  const activeMode = shareModes[modeIndex] ?? shareModes[0];
  const ActiveIcon = SHARE_MODE_ICONS[activeMode.id];
  const expiryDays = EXPIRY_DAYS[expiryIndex] ?? EXPIRY_DAYS[0];

  useEffect(() => {
    if (prefersReducedMotion) return;

    const timer = window.setInterval(() => {
      setModeIndex((current) => (current + 1) % shareModes.length);
    }, CYCLE_MS);

    return () => window.clearInterval(timer);
  }, [prefersReducedMotion, shareModes.length]);

  useEffect(() => {
    if (prefersReducedMotion || activeMode.id !== "expiry") return;

    const timer = window.setInterval(() => {
      setExpiryIndex((current) => (current + 1) % EXPIRY_DAYS.length);
    }, EXPIRY_CYCLE_MS);

    return () => window.clearInterval(timer);
  }, [prefersReducedMotion, activeMode.id]);

  const showWatermark = activeMode.id === "watermark";
  const showGateCard =
    activeMode.id === "password" || activeMode.id === "email";
  const GateIcon = activeMode.id === "password" ? Lock : Mail;

  return (
    <div className="relative flex h-full w-full items-center justify-center">
      <div className="relative h-[80%] w-full max-w-[440px]">
        <motion.div
          className="relative z-10 flex h-full w-full flex-col overflow-hidden rounded-xl border border-zinc-200/50 bg-card shadow-lg dark:border-white/[0.05] dark:bg-[#14161c] dark:shadow-black/30"
          animate={prefersReducedMotion ? undefined : { y: [0, -3, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        >
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-zinc-200/45 bg-muted/40 px-3 py-2 dark:border-white/[0.06] dark:bg-[#101218]">
            <div className="flex min-w-0 flex-col gap-0.5">
              <p className="truncate font-sans text-xs font-semibold text-foreground dark:text-white/90">
                {scriptProtectionVisual.draftTitle}
              </p>
              <p className="font-sans text-[9px] uppercase tracking-[0.14em] text-muted-foreground dark:text-white/35">
                {scriptProtectionVisual.externalShareView}
              </p>
            </div>
            <span className="flex shrink-0 items-center gap-1.5 rounded-full border border-emerald-400/25 bg-emerald-400/10 px-2.5 py-1 font-sans text-[10px] font-medium text-emerald-600 dark:text-emerald-400">
              <span className="size-1.5 rounded-full bg-emerald-500" />
              {scriptProtectionVisual.productionLock}
            </span>
          </div>

          <div
            className={`relative flex min-h-0 flex-1 flex-col overflow-hidden bg-zinc-50 px-4 py-3 dark:bg-[#1a1c22] sm:px-5 sm:py-3.5 ${SCREENPLAY_PAGE}`}
          >
            {showWatermark ? (
              <div
                className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center overflow-hidden"
                aria-hidden
              >
                <span className="rotate-[-24deg] select-none font-['Courier_New',Courier,monospace] text-base font-bold uppercase tracking-[0.4em] text-zinc-900/[0.06] dark:text-white/[0.07] sm:text-lg">
                  {scriptProtectionVisual.watermarkEmail}
                </span>
              </div>
            ) : null}

            <p className="absolute right-5 top-4 font-sans text-[9px] tabular-nums text-muted-foreground dark:text-white/30">
              {scriptProtectionVisual.pageNumber}
            </p>

            <AnimatePresence>
              {showGateCard ? (
                <motion.div
                  key={activeMode.id}
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.2 }}
                  className="absolute left-1/2 top-6 z-20 flex -translate-x-1/2 items-center gap-2 rounded-lg border border-zinc-200/50 dark:border-white/[0.05] bg-card/95 px-3 py-1.5 font-sans text-[10px] text-foreground shadow-lg backdrop-blur-sm  dark:bg-[#101218]/95 dark:text-white/80 dark:shadow-black/40"
                >
                  <GateIcon
                    className="size-3 shrink-0 text-brand"
                    aria-hidden
                  />
                  <span className="whitespace-nowrap font-medium">
                    {activeMode.label}
                  </span>
                </motion.div>
              ) : null}
            </AnimatePresence>

            <div className="flex flex-col gap-[3px]">
              {visibleScript.map((element) => (
                <ScreenplayElement key={element.text} element={element} />
              ))}
            </div>

            <div className="relative mt-1 flex min-h-[88px] flex-1 flex-col overflow-hidden">
              <div
                className="flex flex-col gap-[3px] blur-[2.5px] select-none"
                aria-hidden
              >
                {maskedScript.map((element) => (
                  <ScreenplayElement
                    key={element.text}
                    element={element}
                    className="opacity-70"
                  />
                ))}
              </div>

              <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-zinc-50/20 via-zinc-50/85 to-zinc-50 dark:from-[#1a1c22]/20 dark:via-[#1a1c22]/85 dark:to-[#1a1c22]" />

              <div className="absolute inset-x-0 bottom-0 top-[8%] flex flex-col items-center justify-center gap-2 px-6">
                <div className="flex size-9 items-center justify-center rounded-full border border-zinc-200/50 bg-card shadow-sm dark:border-white/[0.05] dark:bg-[#1a1c22]">
                  <Lock
                    className="size-3.5 text-muted-foreground dark:text-white/50"
                    aria-hidden
                  />
                </div>
                <p className="text-center font-sans text-[9px] font-semibold uppercase tracking-[0.16em] text-muted-foreground dark:text-white/45">
                  {scriptProtectionVisual.maskedPagesLabel}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          className={`absolute -bottom-1 -left-3 z-30 w-[72%] max-w-[220px] ${trustFloatingCardClass}`}
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          onAnimationComplete={() =>
            setShareLinkEntered((entered) => entered || true)
          }
          animate={
            shareLinkEntered && !prefersReducedMotion
              ? { y: [0, -2] }
              : undefined
          }
          transition={
            shareLinkEntered && !prefersReducedMotion
              ? { y: SHARE_LINK_FLOAT_TRANSITION }
              : {
                  opacity: { duration: 0.35 },
                  y: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
                }
          }
        >
          <div className="border-b border-zinc-200/45 px-3 py-2 dark:border-white/[0.06]">
            <p className="font-sans text-[9px] font-semibold uppercase tracking-[0.14em] text-muted-foreground dark:text-white/40">
              {scriptProtectionVisual.shareLinkHeader}
            </p>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeMode.id}
              initial={{ opacity: 0, x: 8 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -8 }}
              transition={{ duration: 0.25 }}
              className="flex items-center gap-2.5 bg-muted/30 p-3 dark:bg-[#101218]"
            >
              <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-zinc-100 dark:bg-white/[0.06]">
                <ActiveIcon className="size-3.5 text-brand" aria-hidden />
              </div>
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <p className="font-sans text-[10px] font-semibold text-foreground dark:text-white/90">
                  {activeMode.id === "expiry" ? (
                    <>
                      {scriptProtectionVisual.expiresIn}{" "}
                      <motion.span
                        key={expiryDays}
                        initial={{ opacity: 0, y: 4 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-brand"
                      >
                        {scriptProtectionVisual.formatExpiresDays(expiryDays)}
                      </motion.span>
                    </>
                  ) : (
                    activeMode.label
                  )}
                </p>
                <p className="font-sans text-[9px] text-muted-foreground dark:text-white/45">
                  {activeMode.detail}
                </p>
              </div>
            </motion.div>
          </AnimatePresence>

          <div className="flex gap-1 border-t border-zinc-200/45 px-3 py-2 dark:border-white/[0.06]">
            {shareModes.map((mode, index) => (
              <span
                key={mode.id}
                className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
                  index === modeIndex
                    ? "bg-zinc-600 dark:bg-brand"
                    : "bg-muted-foreground/20 dark:bg-white/10"
                }`}
                aria-hidden
              />
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
};
